import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { SearchInput, getInputSkeletonFocus, getInputSkeletonSelection, getInputSkeletonQuery, removeInputSkeletonState, getSessionItem, SEARCH_QUERY_KEY, EnlargedSearchInput } from '@atlassian/search-dialog';
import React, { Fragment, useCallback, Suspense } from 'react';
import { injectIntl } from 'react-intl-next';
import { KeyboardShortcut, KeyboardShortcutContainer } from './product-search-input.styled';
import { sha256Hash, TextEnteredHandler, useAnalytics } from '../analytics';
import { onAdvancedSearchSelected, onPreQueryScreenViewed, onNonDebouncedSearchTriggered, onXButtonClicked, Trigger } from '../analytics/events';
import { useSearchSessionId } from '../search-session-provider';
import { messages } from '../../messages';
import { mergeRefCallback } from '../../utils/merge-ref-callback';
import { useSessionUserInput } from '../../extensible/user-input-provider';
import { useDialogExpansionContext } from '../../extensible/dialog-expansion-context';
import { useProductContext } from '../../extensible/product-router';
import { useQuery } from '../../extensible/query-context';
import { usePersistRecentSearches } from '../../extensible/recent-searches/persist-recent-search';
import { useFeaturesContext } from '../../extensible/features-context/features-context';
const SearchAIPromptComponent = /*#__PURE__*/React.lazy(() => import( /* webpackChunkName: "@atlaskit-internal_@atlassian/search-ai/prompt" */'@atlassian/search-ai/prompt').then(({
  SearchAIPrompt
}) => ({
  default: SearchAIPrompt
})));
const AskAIButton = () => {
  const {
    query
  } = useQuery();
  const queryHash = sha256Hash(query);
  const {
    getActiveProduct
  } = useProductContext();
  const activeProduct = getActiveProduct();
  const searchSessionId = useSearchSessionId();
  const {
    showSearchAIDialog,
    toggleShowSearchAIDialog
  } = useSessionUserInput();
  const {
    isExpanded
  } = useDialogExpansionContext();
  return !!(activeProduct !== null && activeProduct !== void 0 && activeProduct.aiSearchConfig) || !!showSearchAIDialog && isExpanded ? /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(Fragment, null)
  }, /*#__PURE__*/React.createElement(SearchAIPromptComponent, {
    query: query,
    queryHash: queryHash,
    searchSessionId: searchSessionId,
    onToggleShowSearchAIDialog: toggleShowSearchAIDialog,
    source: "searchDialog"
  })) : null;
};
const getTooltipContent = message => /*#__PURE__*/React.createElement(KeyboardShortcutContainer, null, message, /*#__PURE__*/React.createElement(KeyboardShortcut, null, '/'));

// Visible for testing.
export class BaseProductSearchInput extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "ref", null);
    _defineProperty(this, "onInput", value => {
      if (value !== this.props.value) {
        var _this$props$onInput, _this$props;
        this.expand(Trigger.TEXT_ENTERED);
        (_this$props$onInput = (_this$props = this.props).onInput) === null || _this$props$onInput === void 0 ? void 0 : _this$props$onInput.call(_this$props, value);
      }
    });
    _defineProperty(this, "expand", trigger => {
      const {
        isExpanded,
        onOpen,
        preQueryScreenViewed,
        searchSessionId
      } = this.props;
      if (!isExpanded) {
        onOpen();
        preQueryScreenViewed({
          searchSessionId,
          trigger
        });
      }
    });
    _defineProperty(this, "close", e => {
      const {
        isExpanded,
        onBack
      } = this.props;
      if (isExpanded) {
        onBack === null || onBack === void 0 ? void 0 : onBack(e);
      }
    });
    _defineProperty(this, "onInputClick", () => {
      setTimeout(() => {
        var _this$ref;
        return (_this$ref = this.ref) === null || _this$ref === void 0 ? void 0 : _this$ref.focus();
      });
      this.expand(Trigger.CLICK);
    });
    _defineProperty(this, "onEnter", event => {
      const {
        isExpanded,
        onEnter
      } = this.props;
      if (isExpanded) {
        onEnter === null || onEnter === void 0 ? void 0 : onEnter(event);
      }
    });
    _defineProperty(this, "onClear", () => {
      var _this$props$onClear, _this$props2;
      setTimeout(() => {
        var _this$ref2;
        return (_this$ref2 = this.ref) === null || _this$ref2 === void 0 ? void 0 : _this$ref2.focus();
      });
      (_this$props$onClear = (_this$props2 = this.props).onClear) === null || _this$props$onClear === void 0 ? void 0 : _this$props$onClear.call(_this$props2);
    });
    _defineProperty(this, "onRef", mergeRefCallback(this.props.forwardRef, ref => {
      this.ref = ref;
    }));
  }
  componentDidMount() {
    const {
      onInput,
      stickySearchEnabled
    } = this.props;
    const skeletonFocused = getInputSkeletonFocus();
    const skeletonQuery = getInputSkeletonQuery();
    const {
      selectionStart,
      selectionEnd,
      selectionDirection
    } = getInputSkeletonSelection();
    const stickySearchQuery = getSessionItem(SEARCH_QUERY_KEY) || '';
    if (skeletonFocused) {
      this.expand(Trigger.CLICK);
      onInput === null || onInput === void 0 ? void 0 : onInput(skeletonQuery);
      if (this.ref) {
        var _this$ref3;
        this.ref.value = skeletonQuery;
        this.ref.focus();
        (_this$ref3 = this.ref) === null || _this$ref3 === void 0 ? void 0 : _this$ref3.setSelectionRange(selectionStart, selectionEnd, selectionDirection);
      }
    }
    removeInputSkeletonState();
    if (stickySearchEnabled && stickySearchQuery) {
      onInput === null || onInput === void 0 ? void 0 : onInput(stickySearchQuery);
    }
  }
  render() {
    const {
      value,
      isExpanded,
      expandedPlaceholder,
      collapsedPlaceholder,
      debounceTime,
      intl,
      showAIToggleButton,
      isQuickFind,
      ...rest
    } = this.props;
    const placeholder = isExpanded ? expandedPlaceholder : collapsedPlaceholder;
    const tooltipContent = getTooltipContent(intl.formatMessage(messages.common_search_input_collapsed_placeholder));
    const SearchInputComponent = isQuickFind ? EnlargedSearchInput : SearchInput;
    return /*#__PURE__*/React.createElement(React.Fragment, null, isExpanded ? /*#__PURE__*/React.createElement(TextEnteredHandler, {
      query: value,
      debounceTime: debounceTime
    }) : null, /*#__PURE__*/React.createElement(SearchInputComponent, _extends({}, rest, {
      onClick: this.onInputClick,
      onInput: this.onInput,
      ref: this.onRef,
      isExpanded: isExpanded,
      placeholder: placeholder,
      value: value,
      onEnter: this.onEnter,
      onClear: this.onClear,
      toggleAISearchButton: showAIToggleButton ? /*#__PURE__*/React.createElement(AskAIButton, null) : undefined,
      tooltipContent: tooltipContent,
      exitSearchLabel: intl.formatMessage(messages.common_search_input_exit_button_label),
      clearSearchLabel: intl.formatMessage(messages.common_search_input_clear_button_label)
    })));
  }
}
export const ProductSearchInputWithIntl = injectIntl(BaseProductSearchInput);
export const ProductSearchInput = props => {
  var _getActiveProduct, _getActiveProduct2, _getActiveProduct3, _useFeaturesContext$q;
  const [_ignore, persistRecentSearchOnEnter] = usePersistRecentSearches();
  const {
    actionSubjectId,
    advancedSearchURL,
    secondaryAdvancedSearchURL,
    isLoading,
    onNavigate,
    ...rest
  } = props;
  const {
    fireAnalyticsEvent
  } = useAnalytics();
  const searchSessionId = useSearchSessionId();
  const {
    stickySearchEnabled,
    query,
    resetSearchSession,
    showSearchAIDialog
  } = useSessionUserInput();
  const {
    allowChangeExpand
  } = useDialogExpansionContext();
  const {
    getActiveProduct
  } = useProductContext();
  const showSearchOnEnter = !!((_getActiveProduct = getActiveProduct()) !== null && _getActiveProduct !== void 0 && _getActiveProduct.showSearchOnEnter);
  const invokePostQuerySearchRef = (_getActiveProduct2 = getActiveProduct()) === null || _getActiveProduct2 === void 0 ? void 0 : _getActiveProduct2.invokePostQuerySearchRef;
  const aiSearchEnabled = !!((_getActiveProduct3 = getActiveProduct()) !== null && _getActiveProduct3 !== void 0 && _getActiveProduct3.aiSearchConfig) || !!showSearchAIDialog;
  const isQuickFind = (_useFeaturesContext$q = useFeaturesContext().quickFind) === null || _useFeaturesContext$q === void 0 ? void 0 : _useFeaturesContext$q.enabled;
  const onEnter = useCallback(event => {
    persistRecentSearchOnEnter(event);
    if (showSearchAIDialog) {
      return;
    }
    if (showSearchOnEnter && !(event !== null && event !== void 0 && event.shiftKey)) {
      if (invokePostQuerySearchRef) {
        fireAnalyticsEvent(onNonDebouncedSearchTriggered());
        invokePostQuerySearchRef.current();
      }
      return;
    }
    const modifierKeyPressed = navigator.userAgent.includes('Mac') ? event.metaKey : event.ctrlKey;
    fireAnalyticsEvent(onAdvancedSearchSelected({
      actionSubjectId,
      isLoading,
      newTab: false,
      trigger: modifierKeyPressed ? Trigger.SHORTCUT_MODIFIED : Trigger.SHORTCUT
    }));
    if (stickySearchEnabled && advancedSearchURL) {
      resetSearchSession();
    }
    onNavigate(modifierKeyPressed && secondaryAdvancedSearchURL ? secondaryAdvancedSearchURL : advancedSearchURL, event);
  }, [persistRecentSearchOnEnter, showSearchAIDialog, showSearchOnEnter, fireAnalyticsEvent, actionSubjectId, isLoading, stickySearchEnabled, advancedSearchURL, onNavigate, secondaryAdvancedSearchURL, invokePostQuerySearchRef, resetSearchSession]);
  const onClear = useCallback(() => {
    fireAnalyticsEvent(onXButtonClicked({
      actionSubjectId,
      searchSessionId
    }));
    resetSearchSession();
    allowChangeExpand(true);
  }, [fireAnalyticsEvent, actionSubjectId, searchSessionId, resetSearchSession, allowChangeExpand]);
  const onPreQueryScreenViewedCB = useCallback(preQueryProps => fireAnalyticsEvent({
    ...onPreQueryScreenViewed(preQueryProps),
    actionSubject: actionSubjectId
  }), [fireAnalyticsEvent, actionSubjectId]);
  return /*#__PURE__*/React.createElement(ProductSearchInputWithIntl, _extends({}, rest, {
    onEnter: onEnter,
    searchSessionId: searchSessionId,
    preQueryScreenViewed: onPreQueryScreenViewedCB,
    onClear: onClear,
    showClearButton: stickySearchEnabled && query !== '',
    allowChangeExpand: allowChangeExpand,
    stickySearchEnabled: stickySearchEnabled,
    showSearchOnEnter: showSearchOnEnter,
    showAIToggleButton: aiSearchEnabled,
    isLoading: isLoading,
    onNavigate: onNavigate,
    isQuickFind: isQuickFind
  }));
};